import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const Plans = () => {
  const navigate = useNavigate();
  const plans = [
    {
      name: 'Cloud',
      pricingType: 'Volume-Based Pricing',
      hosting: 'Self-hosted (you host)',
      description: 'For practitioners looking for fully-managed pipelines without infrastructure hassles.',
      buttonText: 'Start Free Trial',
      buttonVariant: 'white',
      linkText: 'Learn More',
      gradient: 'linear-gradient(180deg, #8125F9 0%, #4649FB 100%)',
      textColor: '#FFFFFF',
      isDark: true,
      isActive: false,
    },
    {
      name: 'Teams',
      pricingType: 'Capacity-Based Pricing',
      hosting: 'Self-hosted (you host)',
      description: 'For organisations that need scalability, governance, and security while simplifying pipeline management.',
      buttonText: 'Start Free Trial',
      buttonVariant: 'purple',
      linkText: 'Learn More',
      gradient: 'linear-gradient(152.63deg, #B7AEFF -65.47%, #EFF6FF 124.76%)',
      textColor: '#4C1D95',
      isDark: false,
      isActive: true,
    },
    {
      name: 'Enterprise',
      pricingType: 'Capacity-Based Pricing',
      hosting: 'Self-hosted (you host)',
      description: 'For organizations needing security, compliance, and full infrastructure control. Capacity-based pricing.',
      buttonText: 'Contact Us',
      buttonVariant: 'purple',
      linkText: null,
      gradient: 'linear-gradient(152.63deg, #B7AEFF -65.47%, #EFF6FF 124.76%)',
      textColor: '#4C1D95',
      isDark: false,
      isActive: false,
    },
  ];

  return (
    <Box
      sx={{
        width: '100vw',
        py: { xs: 8, md: 12 },
        px: { xs: 2, sm: 3, md: 4 },
        background: 'linear-gradient(180deg, #0F0F1A 0%, #1A0F2A 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="xl">
        {/* Header Section */}
        <Box
          sx={{
            textAlign: 'center',
            mb: { xs: 6, md: 8 },
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h2"
              sx={{
                fontFamily: 'Sora, sans-serif',
                fontWeight: 700,
                fontSize: { xs: '32px', sm: '40px', md: '48px' },
                lineHeight: '100%',
                letterSpacing: '0%',
                textAlign: 'center',
                background: 'linear-gradient(180deg, #F6F6F7 0%, #7E808F 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                mb: 2,
              }}
            >
              Plans that scale with your data
            </Typography>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Typography
              sx={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 400,
                fontSize: { xs: '14px', sm: '15px', md: '16px' },
                lineHeight: '160%',
                letterSpacing: '0px',
                textAlign: 'center',
                color: '#FFFFFFCC',
                maxWidth: '800px',
                mx: 'auto',
                px: { xs: 1, sm: 0 },
              }}
            >
              One platform for ETL, Log Transformation, and DB Migration—priced for teams that need predictable costs and enterprise guardrails.
            </Typography>
          </motion.div>
        </Box>

        {/* Plans Cards */}
        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ mt: { xs: 3, md: 4 } }}>
          {plans.map((plan, index) => (
            <Grid item xs={12} sm={6} md={4} key={plan.name}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    background: plan.gradient,
                    borderRadius: '4px',
                    p: { xs: 2.5, sm: 3, md: 4 },
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: { xs: 'auto', sm: '330px', md: '400px' },
                  }}
                >
                  {/* Plan Name */}
                  <Typography
                    sx={{
                      fontFamily: 'Sora, sans-serif',
                      fontWeight: 700,
                      fontSize: { xs: '22px', sm: '24px', md: '26px' },
                      lineHeight: '100%',
                      letterSpacing: '-3%',
                      textAlign: 'center',
                      color: plan.textColor,
                      mb: { xs: 0.5, md: 1 },
                    }}
                  >
                    {plan.name}
                  </Typography>

                  {/* Pricing Type */}
                  <Typography
                    sx={{
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 500,
                      fontSize: { xs: '16px', sm: '17px', md: '18px' },
                      lineHeight: '100%',
                      letterSpacing: '-3%',
                      textAlign: 'center',
                      color: plan.isDark ? '#FFFFFFE5' : plan.textColor,
                      mb: { xs: 1.5, md: 2 },
                    }}
                  >
                    {plan.pricingType}
                  </Typography>

                  {/* Hosting */}
                  <Typography
                    sx={{
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 600,
                      fontSize: { xs: '14px', sm: '15px', md: '16px' },
                      lineHeight: '140%',
                      letterSpacing: '0%',
                      textAlign: 'center',
                      color: plan.textColor,
                      mb: { xs: 2, md: 3 },
                    }}
                  >
                    {plan.hosting}
                  </Typography>

                  {/* Description */}
                  <Typography
                    sx={{
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 500,
                      fontSize: { xs: '14px', sm: '15px', md: '16px' },
                      lineHeight: '100%',
                      letterSpacing: '-3%',
                      textAlign: 'center',
                      color: plan.isDark ? '#FFFFFFCC' : plan.textColor,
                      mb: { xs: 3, md: 4 },
                      flex: 1,
                    }}
                  >
                    {plan.description}
                  </Typography>

                  {/* Buttons */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2 }, alignItems: 'center' }}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        if (plan.buttonText === 'Start Free Trial') {
                          navigate('/trial');
                        } else if (plan.buttonText === 'Contact Us') {
                          navigate('/contact');
                        }
                      }}
                      sx={{
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 600,
                        fontSize: { xs: '14px', sm: '15px', md: '16px' },
                        lineHeight: '24px',
                        letterSpacing: '-0.31px',
                        textAlign: 'center',
                        width: { xs: '100%', sm: '170px' },
                        height: { xs: '44px', md: '48px' },
                        borderRadius: '100px',
                        background: plan.isDark 
                          ? '#FFFFFF' 
                          : 'linear-gradient(90deg, #8225FA 0%, #434BFB 100%)',
                        color: plan.isDark ? '#000000' : '#FFFFFF',
                        textTransform: 'none',
                        boxShadow: '0px 4px 6px -4px #3156FB40, 0px 10px 15px -3px #3156FB40',
                        '&:hover': {
                          background: plan.isDark 
                            ? '#F3F4F6' 
                            : 'linear-gradient(90deg, #6D1ED9 0%, #3A3FE8 100%)',
                        },
                      }}
                    >
                      {plan.buttonText}
                    </Button>

                    {plan.linkText && (
                      <Button
                        variant="text"
                        sx={{
                          fontFamily: 'Sora, sans-serif',
                          fontWeight: 600,
                          fontSize: { xs: '14px', sm: '15px', md: '16px' },
                          lineHeight: '16px',
                          letterSpacing: '0%',
                          color: plan.isDark ? '#FFFFFF' : plan.textColor,
                          textTransform: 'none',
                          py: { xs: 0.5, md: 1 },
                          '&:hover': {
                            backgroundColor: 'transparent',
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {plan.linkText}
                      </Button>
                    )}
                  </Box>
                </Paper>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Plans;
